import {
  startTradeApplication,
  getTradeApplication,
  saveTradeApplicationDraft,
  assignTradeApplicationDocument,
  submitTradeApplication,
  ApplicationDocumentSlot,
  ApplicationMediaType,
} from "../../api/tradeApplicationController";
import { uploadEntityMediaFile } from "../../api/entityMediaController";

// DeedOfSuretySignature / DirectorSuretySignature are always canvas-drawn PNGs, and the backend's slot
// validation requires MediaType.Signature specifically for them — a PDF-vs-image sniff on the file's MIME
// type would never produce that value.
const SIGNATURE_SLOTS = [
  ApplicationDocumentSlot.DeedOfSuretySignature,
  ApplicationDocumentSlot.DirectorSuretySignature,
];

// Turns the controller's status flags into a friendly summary for setErrorMessage. Web convention: no toasts;
// pages read ApiCallStatus + an error-message string (+ optional per-field errors).
const messageFor = (response, fallback) => {
  if (response?.conflict) return "This application has already been submitted or has changed. Please reload.";
  if (response?.forbidden || response?.notFound) return "This resume link is invalid or has expired.";
  // response.message now carries the real, joined validation text (not a generic fallback).
  if (response?.invalid) return response?.message || "Please check the details and try again.";
  return response?.message || fallback;
};

// Surfaces a failed response to the page WITHOUT swallowing validation detail: the field-level error map
// (response.errors, e.g. { Email: ["Email is required"] }) goes to setFieldErrors for per-field display, and a
// ready-to-show summary goes to setErrorMessage. The FE team can use either or both.
const reportFailure = (response, fallback, { setErrorMessage, setFieldErrors, setStatusFailed }) => {
  if (setFieldErrors) setFieldErrors(response?.errors ?? null);
  if (setErrorMessage) setErrorMessage(messageFor(response, fallback));
  setStatusFailed();
};

// Start a draft. On success, hands the whole start payload (referenceNumber + one-time resumeToken) to
// setApplication — the page persists the reference + token to build the resume link and continue the form.
export const startApplication = async ({
  request,
  captchaToken,
  setApplication,
  setStatusInProgress,
  setStatusSuccess,
  setStatusFailed,
  setStatusError,
  setErrorMessage,
  setFieldErrors,
}) => {
  setStatusInProgress();
  try {
    const response = await startTradeApplication({ request, captchaToken });
    if (response?.success) {
      if (setApplication) setApplication(response.data);
      setStatusSuccess();
    } else {
      reportFailure(response, "Could not start the application.", { setErrorMessage, setFieldErrors, setStatusFailed });
    }
  } catch (error) {
    console.error(error);
    setStatusError();
  }
};

// Load a draft by reference + resume token.
export const fetchApplication = async ({
  reference,
  resumeToken,
  setApplication,
  setStatusInProgress,
  setStatusSuccess,
  setStatusFailed,
  setStatusError,
  setErrorMessage,
  setFieldErrors,
}) => {
  setStatusInProgress();
  try {
    const response = await getTradeApplication({ reference, resumeToken });
    if (response?.success) {
      if (setApplication) setApplication(response.data);
      setStatusSuccess();
    } else {
      reportFailure(response, "Could not load the application.", { setErrorMessage, setFieldErrors, setStatusFailed });
    }
  } catch (error) {
    console.error(error);
    setStatusError();
  }
};

// Save partial progress. `request` is the desired full state (the server reconciles directors).
export const saveApplicationDraft = async ({
  reference,
  resumeToken,
  request,
  setStatusInProgress,
  setStatusSuccess,
  setStatusFailed,
  setStatusError,
  setErrorMessage,
  setFieldErrors,
}) => {
  setStatusInProgress();
  try {
    const response = await saveTradeApplicationDraft({ reference, resumeToken, request });
    if (response?.success) {
      setStatusSuccess();
    } else {
      reportFailure(response, "Could not save the application.", { setErrorMessage, setFieldErrors, setStatusFailed });
    }
  } catch (error) {
    console.error(error);
    setStatusError();
  }
};

// Upload a document file AND attach it to a slot in one call: uploadEntityMediaFile (reserve -> SAS PUT ->
// verify) then assignTradeApplicationDocument. The caller supplies the raw File; everything else is hidden.
export const uploadApplicationDocument = async ({
  reference,
  resumeToken,
  slot,
  directorKey = null,
  file,
  mediaTypeId = SIGNATURE_SLOTS.includes(slot)
    ? ApplicationMediaType.Signature
    : file?.type === "application/pdf"
      ? ApplicationMediaType.Document
      : ApplicationMediaType.Image,
  setStatusInProgress,
  setStatusSuccess,
  setStatusFailed,
  setStatusError,
  setErrorMessage,
  setFieldErrors,
}) => {
  setStatusInProgress();
  try {
    const upload = await uploadEntityMediaFile({ file, mediaTypeId });
    if (!upload?.success) {
      // Surface file-policy validation (type/size) instead of a generic "upload failed".
      reportFailure(upload, "The file could not be uploaded.", { setErrorMessage, setFieldErrors, setStatusFailed });
      return;
    }

    const response = await assignTradeApplicationDocument({
      reference,
      resumeToken,
      slot,
      submissionId: upload.submissionId,
      directorKey,
    });
    if (response?.success) {
      setStatusSuccess();
    } else {
      reportFailure(response, "Could not attach the document.", { setErrorMessage, setFieldErrors, setStatusFailed });
    }
  } catch (error) {
    console.error(error);
    setStatusError();
  }
};

// Submit the completed application. A 409 is surfaced as a distinct "already submitted" message; a 400 surfaces
// the domain's submit-time validation (missing required fields/documents).
export const submitApplication = async ({
  reference,
  resumeToken,
  setStatusInProgress,
  setStatusSuccess,
  setStatusFailed,
  setStatusError,
  setErrorMessage,
  setFieldErrors,
}) => {
  setStatusInProgress();
  try {
    const response = await submitTradeApplication({ reference, resumeToken });
    if (response?.success) {
      setStatusSuccess();
    } else {
      reportFailure(response, "Could not submit the application.", { setErrorMessage, setFieldErrors, setStatusFailed });
    }
  } catch (error) {
    console.error(error);
    setStatusError();
  }
};
